import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Plus, Play, Pause, Trash2, RefreshCw, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { schedulerApi, type ScheduledTrigger } from '@/lib/scheduler-api';
import { TriggerCreateDrawer } from '@/components/admin/scheduler/TriggerCreateDrawer';
import { usePageTitle } from '@/hooks/use-page-title';
import { useHeaderAction } from '@/lib/header-action-context';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { listControlsGlassTrigger } from '@/components/capability-list/list-controls-classes';
import { cn } from '@/lib/utils';

const primaryButtonClass =
    'inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2';

type TabKey = 'all' | 'one_shot' | 'recurring' | 'active' | 'paused';

const TABS: { key: TabKey; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'one_shot', label: 'One-shot' },
    { key: 'recurring', label: 'Recurring' },
    { key: 'active', label: 'Active' },
    { key: 'paused', label: 'Paused' },
];

const SORT_OPTIONS = [
    { key: 'name', label: 'Name' },
    { key: 'nextRun', label: 'Next Run' },
    { key: 'lastRun', label: 'Last Run' },
    { key: 'created', label: 'Created' },
];

function formatSchedule(t: ScheduledTrigger): string {
    if (t.triggerType === 'one_shot') {
        return t.delayUntilAt ? `Once at ${new Date(t.delayUntilAt).toLocaleString()}` : 'One-shot';
    }
    if (t.scheduleKind === 'cron' && t.cronExpression) return `Cron: ${t.cronExpression}`;
    if (t.scheduleKind === 'rrule' && t.rruleExpression) return `RRULE: ${t.rruleExpression.slice(0, 40)}...`;
    return t.triggerType;
}

function statusBadge(t: ScheduledTrigger) {
    if (!t.enabled)
        return <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100 text-[10px] font-medium">Paused</Badge>;
    if (t.nextRunAt)
        return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] font-medium">Active</Badge>;
    return <Badge className="bg-sky-100 text-sky-600 hover:bg-sky-100 text-[10px] font-medium">Idle</Badge>;
}

function matchesTab(t: ScheduledTrigger, tab: TabKey): boolean {
    switch (tab) {
        case 'all':
            return true;
        case 'one_shot':
            return t.triggerType === 'one_shot';
        case 'recurring':
            return t.triggerType === 'recurring';
        case 'active':
            return t.enabled;
        case 'paused':
            return !t.enabled;
    }
}

function compareTriggers(a: ScheduledTrigger, b: ScheduledTrigger, field: string, dir: string): number {
    const mul = dir === 'asc' ? 1 : -1;
    switch (field) {
        case 'name':
            return mul * a.name.localeCompare(b.name);
        case 'nextRun':
            return mul * ((a.nextRunAt ?? '').localeCompare(b.nextRunAt ?? ''));
        case 'lastRun':
            return mul * ((a.lastRunAt ?? '').localeCompare(b.lastRunAt ?? ''));
        case 'created':
            return mul * a.created.localeCompare(b.created);
        default:
            return 0;
    }
}

export default function TriggerListPage() {
    usePageTitle('Scheduled Triggers');
    const navigate = useNavigate();
    const { setHeaderAction, setHeaderTitle } = useHeaderAction();
    const [triggers, setTriggers] = useState<ScheduledTrigger[]>([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState<TabKey>('all');
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [targetActionFilter, setTargetActionFilter] = useState('all');

    const loadTriggers = async () => {
        setLoading(true);
        try {
            const res = await schedulerApi.listTriggers();
            setTriggers(res.data.data ?? []);
        } catch {
            setTriggers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadTriggers(); }, []);

    useEffect(() => {
        setHeaderTitle({
            icon: (
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-100 text-violet-700">
                    <Timer size={16} />
                </div>
            ),
            title: 'Scheduled Triggers',
            subtitle: 'Manage one-shot timers, cron jobs, and recurring schedules.',
        });
        setHeaderAction(
            <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={loadTriggers}>
                    <RefreshCw className="mr-1 h-4 w-4" /> Refresh
                </Button>
                <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    className={primaryButtonClass}
                >
                    <Plus size={14} />
                    New Trigger
                </button>
            </div>,
        );
        return () => {
            setHeaderAction(null);
            setHeaderTitle(null);
        };
    }, [setHeaderAction, setHeaderTitle]);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const filteredTriggers = useMemo(() => {
        let result = triggers.filter((t) => matchesTab(t, activeTab));

        if (targetActionFilter !== 'all') {
            result = result.filter((t) => t.targetAction === targetActionFilter);
        }

        const q = searchQuery.trim().toLowerCase();
        if (q) {
            result = result.filter(
                (t) =>
                    t.name.toLowerCase().includes(q) ||
                    t.targetRef.toLowerCase().includes(q) ||
                    formatSchedule(t).toLowerCase().includes(q),
            );
        }

        return [...result].sort((a, b) => compareTriggers(a, b, sortField, sortDirection));
    }, [triggers, searchQuery, activeTab, targetActionFilter, sortField, sortDirection]);

    const handleToggle = async (t: ScheduledTrigger) => {
        try {
            if (t.enabled) {
                await schedulerApi.pauseTrigger(t.id);
            } else {
                await schedulerApi.resumeTrigger(t.id);
            }
            loadTriggers();
        } catch { /* ignore */ }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Delete this trigger? This cannot be undone.')) return;
        try {
            await schedulerApi.deleteTrigger(id);
            loadTriggers();
        } catch { /* ignore */ }
    };

    const handleFire = async (id: string) => {
        try {
            await schedulerApi.fireTrigger(id);
            loadTriggers();
        } catch { /* ignore */ }
    };

    const tabCounts = useMemo(() => {
        const counts: Record<TabKey, number> = { all: 0, one_shot: 0, recurring: 0, active: 0, paused: 0 };
        for (const t of triggers) {
            counts.all++;
            if (t.triggerType === 'one_shot') counts.one_shot++;
            if (t.triggerType === 'recurring') counts.recurring++;
            if (t.enabled) counts.active++;
            if (!t.enabled) counts.paused++;
        }
        return counts;
    }, [triggers]);

    const tabBar = (
        <div className="flex items-center rounded-md border border-[hsl(var(--list-controls-border))] bg-[hsl(var(--list-controls-surface))] p-1">
            {TABS.map((tab) => (
                <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={cn(
                        'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                        activeTab === tab.key
                            ? 'bg-[hsl(var(--list-controls-sort-active-bg))] text-[hsl(var(--list-controls-sort-active-fg))] shadow-sm'
                            : 'text-[hsl(var(--list-controls-muted-foreground))] hover:bg-[hsl(var(--list-controls-surface-hover))] hover:text-[hsl(var(--list-controls-foreground))]',
                    )}
                >
                    {tab.label}
                    <span
                        className={cn(
                            'rounded-full px-1.5 py-0.5 text-xs font-medium',
                            activeTab === tab.key
                                ? 'bg-white/20 text-[hsl(var(--list-controls-sort-active-fg))]'
                                : 'bg-[hsl(var(--list-controls-border))] text-[hsl(var(--list-controls-muted-foreground))]',
                        )}
                    >
                        {tabCounts[tab.key]}
                    </span>
                </button>
            ))}
        </div>
    );

    const targetActionDropdown = (
        <Select value={targetActionFilter} onValueChange={setTargetActionFilter}>
            <SelectTrigger className={cn('w-48', listControlsGlassTrigger)}>
                <SelectValue placeholder="All actions" />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                <SelectItem value="invoke_capability">Invoke Capability</SelectItem>
                <SelectItem value="signal_workflow">Signal Workflow</SelectItem>
            </SelectContent>
        </Select>
    );

    return (
        <>
            <CapabilityListPanel<ScheduledTrigger>
                items={filteredTriggers.length > 0 ? [filteredTriggers[0]] : []}
                sortOptions={SORT_OPTIONS}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Search triggers by name, target, or schedule..."
                allTags={[]}
                selectedTags={new Set<string>()}
                onToggleTag={() => {}}
                onClearAllTags={() => {}}
                onSelectAllTags={() => {}}
                showTagsFilter={false}
                isLoading={loading}
                loadingMessage="Loading triggers..."
                emptyStateVariant="glass"
                emptyMessage="No triggers yet. Create your first scheduled trigger."
                gridClassName="grid grid-cols-1 h-full"
                gridStyle={{}}
                contentClassName="flex-1 px-16 pb-6 flex flex-col"
                extraToolbar={
                    <div className="flex items-center gap-3">
                        {tabBar}
                        {targetActionDropdown}
                    </div>
                }
                toolbarClassName="flex flex-col gap-4 p-6"
                renderCard={() => (
                    <div key="triggers-table" className="h-full w-full rounded-md border border-white/30 bg-white/40 shadow-sm overflow-auto">
                        <Table>
                            <TableHeader className="bg-gray-200">
                                <TableRow className="border-gray-200">
                                    <TableHead>Name</TableHead>
                                    <TableHead>Target</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Schedule</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Next Run</TableHead>
                                    <TableHead>Last Run</TableHead>
                                    <TableHead className="text-right">Runs</TableHead>
                                    <TableHead className="w-[120px]">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredTriggers.map((t) => (
                                    <TableRow
                                        key={t.id}
                                        className="bg-white border-gray-200 hover:bg-blue-50 cursor-pointer"
                                        onClick={() => navigate(`/scheduler/triggers/${t.id}`)}
                                    >
                                        <TableCell>
                                            <Link
                                                to={`/scheduler/triggers/${t.id}`}
                                                className="text-sm font-medium text-foreground hover:text-primary hover:underline"
                                            >
                                                {t.name}
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            <span className="max-w-[200px] truncate font-mono text-xs text-muted-foreground">
                                                {t.targetRef}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                className={cn(
                                                    'text-[10px] font-medium',
                                                    t.triggerType === 'one_shot'
                                                        ? 'bg-amber-100 text-amber-700 hover:bg-amber-100'
                                                        : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-100',
                                                )}
                                            >
                                                {t.triggerType === 'one_shot' ? 'One-shot' : 'Recurring'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {formatSchedule(t)}
                                        </TableCell>
                                        <TableCell>{statusBadge(t)}</TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {t.nextRunAt ? new Date(t.nextRunAt).toLocaleString() : '—'}
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {t.lastRunAt ? new Date(t.lastRunAt).toLocaleString() : '—'}
                                        </TableCell>
                                        <TableCell className="text-right text-xs text-muted-foreground">
                                            {t.runCount}
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7"
                                                    title={t.enabled ? 'Pause' : 'Resume'}
                                                    onClick={() => handleToggle(t)}
                                                >
                                                    {t.enabled ? (
                                                        <Pause className="h-3.5 w-3.5" />
                                                    ) : (
                                                        <Play className="h-3.5 w-3.5" />
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7"
                                                    title="Fire Now"
                                                    onClick={() => handleFire(t.id)}
                                                >
                                                    <RefreshCw className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 text-destructive hover:text-destructive"
                                                    title="Delete"
                                                    onClick={() => handleDelete(t.id)}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            />
            <TriggerCreateDrawer
                open={drawerOpen}
                onOpenChange={setDrawerOpen}
                onSuccess={loadTriggers}
            />
        </>
    );
}
